import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useWalletProvider } from '../walletprovider/useWalletProvider';
import { hasProtectedWalletAccess } from '../walletprovider/walletWhitelist';
import { chatKeySignMessage, bytesToBase64, base64ToBytes, deriveV2KeyBytesFromSignature } from '../utils/chatCrypto';
import { getOrCreateWalletDeviceIdentity } from '../utils/chatDeviceIdentity';
import { getWalletDeviceSessionToken, listWalletDevices, registerWalletDevice, revokeWalletDevice, } from '../utils/chatDeviceSession';
import { decryptChatVault, deriveInitialChatVaultMasterKey, encryptChatVault, unwrapVaultKeyFromDevice, unwrapVaultKeyFromRecovery, wrapVaultKeyForDevice, wrapVaultKeyForRecovery, } from '../utils/chatVault';
import { loadJwt } from '../utils/dwclient/localstorage/jwtStorage';
import { getMyFilesListJwt, myReadFileJwt, myWriteFileJwt } from '../utils/dwclient/dwstorageutils';
const LOCAL_PREFIX = 'dwdata_chat_vault:';
const unavailable = async () => { throw new Error('Wallet device security is unavailable'); };
export const ChatSecurityContext = createContext({
    wallet: null, identity: null, provider: null, deviceId: null, deviceSessionToken: null, v2KeyBytes: null, keyReady: false,
    status: 'idle', error: null, retryProvision: async () => { }, renewDeviceSession: async () => { },
    listDevices: unavailable, revokeDevice: unavailable,
});
export const useChatSecurity = () => useContext(ChatSecurityContext);
function isBundle(value, wallet, room) {
    const b = value;
    return !!b && b.v === 1 && b.wallet === wallet && b.room === room &&
        !!b.recovery && !!b.device && !!b.vault;
}
function vaultPath(wallet) {
    return `/User Files/.dwinity/private/chat-vaults/${wallet}.json`;
}
export const ChatSecurityProvider = ({ children, walletSource }) => {
    const desktopWallet = useWalletProvider();
    const selectedAccount = walletSource ? walletSource.address : desktopWallet.selectedAccount;
    const walletProvider = walletSource
        ? walletSource.provider || undefined
        : desktopWallet.selectedWallet?.provider;
    const walletIsConnected = walletSource ? walletSource.isConnected : desktopWallet.isConnected();
    // A connected wallet is not yet an authenticated storage session. Mobile
    // supplies its explicit backend-ready flag; desktop must wait for the same
    // authoritative state from WalletProvider before reading or writing StorJ.
    const walletIsAuthenticated = walletSource
        ? walletSource.isAuthenticated === true
        : hasProtectedWalletAccess(desktopWallet.backendAuthStatus, desktopWallet.whitelistStatus);
    const [identity, setIdentity] = useState(null);
    const [deviceSessionToken, setDeviceSessionToken] = useState(null);
    const [v2KeyBytes, setV2KeyBytes] = useState(null);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    const inFlightRef = useRef(null);
    // A ready vault is wallet/device-bound. Provider identity remains mandatory
    // while provisioning is in flight, but AppKit may replace its provider
    // wrapper for the same already-authorized wallet. That object-only refresh
    // must not discard a complete key/session scope and reopen MetaMask.
    const committedRef = useRef(null);
    const generationRef = useRef(0);
    const currentWalletRef = useRef(null);
    const currentProviderRef = useRef(null);
    const currentConnectionRef = useRef(false);
    const currentAuthenticationRef = useRef(false);
    const securityStateRef = useRef({ status, identity, deviceSessionToken, v2KeyBytes });
    currentWalletRef.current = selectedAccount?.toLowerCase() || null;
    currentProviderRef.current = walletProvider || null;
    currentConnectionRef.current = walletIsConnected;
    currentAuthenticationRef.current = walletIsAuthenticated;
    securityStateRef.current = { status, identity, deviceSessionToken, v2KeyBytes };
    const provision = useCallback(async () => {
        const wallet = selectedAccount?.toLowerCase();
        const provider = walletProvider;
        if (!wallet || !provider || !walletIsConnected || !walletIsAuthenticated) {
            ++generationRef.current;
            committedRef.current = null;
            setIdentity(null);
            setDeviceSessionToken(null);
            setV2KeyBytes(null);
            setError(null);
            setStatus('idle');
            return;
        }
        const currentState = securityStateRef.current;
        const committed = committedRef.current;
        if (currentState.status === 'ready' && committed?.wallet === wallet &&
            committed.identity === currentState.identity &&
            committed.token === currentState.deviceSessionToken && committed.key === currentState.v2KeyBytes) {
            if (committed.provider !== provider)
                committedRef.current = { ...committed, provider };
            return;
        }
        // Coalesce StrictMode effects and repeated retries.  A new wallet gets a
        // new generation; old operations are never allowed to commit state.
        if (inFlightRef.current?.wallet === wallet && inFlightRef.current.provider === provider)
            return;
        const generation = ++generationRef.current;
        const isCurrent = () => generation === generationRef.current && currentWalletRef.current === wallet &&
            currentProviderRef.current === provider && currentConnectionRef.current &&
            currentAuthenticationRef.current;
        const requireCurrent = () => { if (!isCurrent())
            throw new Error('Secure chat provisioning was superseded by a wallet change'); };
        inFlightRef.current = { wallet, provider };
        committedRef.current = null;
        setStatus('provisioning');
        setError(null);
        setV2KeyBytes(null);
        setDeviceSessionToken(null);
        try {
            const id = await getOrCreateWalletDeviceIdentity(wallet);
            requireCurrent();
            setIdentity(id);
            let token;
            try {
                token = await getWalletDeviceSessionToken(wallet, id, 'admin_presence');
                requireCurrent();
            }
            catch (sessionError) {
                if (sessionError?.code !== 'device_not_found')
                    throw sessionError;
                const sign = async (message) => {
                    requireCurrent();
                    const signature = await provider.request({ method: 'personal_sign', params: [
                            `0x${Array.from(new TextEncoder().encode(message), x => x.toString(16).padStart(2, '0')).join('')}`, wallet,
                        ] });
                    if (typeof signature !== 'string' || !signature)
                        throw new Error('Wallet did not return a registration signature');
                    return signature;
                };
                await registerWalletDevice(wallet, id, sign, 'admin_presence');
                requireCurrent();
                token = await getWalletDeviceSessionToken(wallet, id, 'admin_presence');
                requireCurrent();
            }
            requireCurrent();
            setDeviceSessionToken(token);
            const localKey = LOCAL_PREFIX + wallet;
            let bundle = null;
            try {
                const raw = localStorage.getItem(localKey);
                if (raw)
                    bundle = JSON.parse(raw);
            }
            catch {
                throw new Error('Local chat vault is malformed');
            }
            if (bundle && !isBundle(bundle, wallet, wallet))
                throw new Error('Local chat vault does not match this wallet');
            let master;
            if (bundle) {
                master = await unwrapVaultKeyFromDevice(bundle.device, id, wallet);
                requireCurrent();
            }
            else {
                // StorJ contains only this opaque, authenticated bundle. Reading it
                // directly avoids the generic storage encryption/signing path.
                const jwt = await loadJwt();
                requireCurrent();
                if (!jwt)
                    throw new Error('Sign in is required to provision secure chat storage');
                const remotePath = vaultPath(wallet);
                const listing = await getMyFilesListJwt(jwt, '/User Files', true);
                requireCurrent();
                if (!Array.isArray(listing?.entries)) {
                    throw new Error('Secure chat storage returned an invalid file list');
                }
                const remoteExists = listing.entries.some((entry) => entry?.isFile === true && entry.path?.replace(/\/+/g, '/') === remotePath);
                if (remoteExists) {
                    const remote = await myReadFileJwt(jwt, vaultPath(wallet));
                    requireCurrent();
                    bundle = JSON.parse(await remote.text());
                    requireCurrent();
                    if (!isBundle(bundle, wallet, wallet))
                        throw new Error('Remote chat vault does not match this wallet');
                }
                const signRecovery = async () => {
                    const message = chatKeySignMessage(wallet);
                    requireCurrent();
                    const signature = await provider.request({ method: 'personal_sign', params: [
                            `0x${Array.from(new TextEncoder().encode(message), x => x.toString(16).padStart(2, '0')).join('')}`, wallet,
                        ] });
                    if (typeof signature !== 'string' || !signature)
                        throw new Error('Wallet did not return a recovery signature');
                    return signature;
                };
                const recoverySignature = await signRecovery(); // exactly once for recovery + v2 derivation
                requireCurrent();
                if (bundle) {
                    master = await unwrapVaultKeyFromRecovery(bundle.recovery, recoverySignature, wallet);
                    requireCurrent();
                    bundle.device = await wrapVaultKeyForDevice(master, id, wallet);
                }
                else {
                    // Unlike later rotated masters, first creation is deterministic and
                    // wallet-scoped so simultaneous new devices cannot create
                    // incompatible vaults before either one observes the other's write.
                    master = await deriveInitialChatVaultMasterKey(recoverySignature, wallet);
                    const rawV2 = await deriveV2KeyBytesFromSignature(recoverySignature);
                    requireCurrent();
                    bundle = {
                        v: 1, wallet, room: wallet,
                        recovery: await wrapVaultKeyForRecovery(master, recoverySignature, wallet),
                        device: await wrapVaultKeyForDevice(master, id, wallet),
                        vault: await encryptChatVault(JSON.stringify({ v: 2, room: wallet, key: bytesToBase64(rawV2) }), master, wallet, wallet),
                    };
                }
                // Signature is deliberately scoped to this block and never stored/sent.
            }
            const plaintext = await decryptChatVault(bundle.vault, master, wallet, wallet);
            requireCurrent();
            const vault = JSON.parse(plaintext);
            if (vault?.v !== 2 || vault.room !== wallet || typeof vault.key !== 'string')
                throw new Error('Chat vault payload validation failed');
            const raw = base64ToBytes(vault.key);
            if (raw.length !== 32)
                throw new Error('Chat vault key length is invalid');
            localStorage.setItem(localKey, JSON.stringify(bundle));
            const jwt = await loadJwt();
            requireCurrent();
            if (!jwt)
                throw new Error('Sign in is required to synchronize secure chat storage');
            // Re-read first on creation to avoid replacing a concurrent first vault.
            await myWriteFileJwt(jwt, vaultPath(wallet), new File([JSON.stringify(bundle)], 'chat-vault-v1.json', { type: 'application/json' }));
            requireCurrent();
            // Commit the complete scope together.  This is what makes an effect
            // replay (including Android visibility/remount behavior) a no-op.
            committedRef.current = { wallet, provider, identity: id, token, key: raw };
            setV2KeyBytes(raw);
            setStatus('ready');
        }
        catch (cause) {
            if (isCurrent())
                setStatus('error'), setError(cause instanceof Error ? cause.message : 'Secure chat provisioning failed');
        }
        finally {
            if (inFlightRef.current?.wallet === wallet && inFlightRef.current.provider === provider)
                inFlightRef.current = null;
        }
    }, [selectedAccount, walletProvider, walletIsConnected, walletIsAuthenticated]);
    useEffect(() => { void provision(); }, [provision]);
    const renewDeviceSession = useCallback(async () => {
        const wallet = selectedAccount?.toLowerCase();
        const provider = walletProvider;
        if (!wallet || !provider || !walletIsConnected || !walletIsAuthenticated || !identity || identity.wallet !== wallet) {
            throw new Error('Wallet device session cannot be renewed');
        }
        const renewalGeneration = generationRef.current;
        try {
            const token = await getWalletDeviceSessionToken(wallet, identity, 'admin_presence', window.location.origin, true);
            if (renewalGeneration !== generationRef.current ||
                currentWalletRef.current !== wallet ||
                currentProviderRef.current !== provider ||
                !currentConnectionRef.current ||
                !currentAuthenticationRef.current)
                return;
            const committed = committedRef.current;
            if (committed?.wallet === wallet && committed.identity === identity && v2KeyBytes) {
                committedRef.current = { ...committed, provider, token };
            }
            setDeviceSessionToken(token);
            if (v2KeyBytes) {
                setError(null);
                setStatus('ready');
            }
        }
        catch (cause) {
            if (currentWalletRef.current === wallet && currentProviderRef.current === provider) {
                ++generationRef.current;
                committedRef.current = null;
                setIdentity(null);
                setDeviceSessionToken(null);
                setV2KeyBytes(null);
                setStatus('error');
                setError(cause instanceof Error ? cause.message : 'Secure chat device session renewal failed');
            }
            throw cause;
        }
    }, [selectedAccount, walletProvider, walletIsConnected, walletIsAuthenticated, identity, v2KeyBytes]);
    useEffect(() => {
        if (!walletIsAuthenticated || !deviceSessionToken || !identity || status !== 'ready')
            return;
        let cancelled = false;
        let timer;
        try {
            const encodedPayload = deviceSessionToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
            const paddedPayload = encodedPayload.padEnd(Math.ceil(encodedPayload.length / 4) * 4, '=');
            const payload = JSON.parse(atob(paddedPayload));
            const expiresAt = Number(payload.exp) * 1000;
            if (!Number.isFinite(expiresAt))
                throw new Error('Invalid device session expiry');
            const renew = async () => {
                try {
                    await renewDeviceSession();
                }
                catch {
                    if (!cancelled && Date.now() < expiresAt)
                        timer = setTimeout(renew, 15_000);
                }
            };
            timer = setTimeout(renew, Math.max(0, expiresAt - Date.now() - 60_000));
        }
        catch {
            void renewDeviceSession().catch(() => { });
        }
        return () => { cancelled = true; if (timer)
            clearTimeout(timer); };
    }, [walletIsAuthenticated, deviceSessionToken, identity, status, renewDeviceSession]);
    const committed = committedRef.current;
    const scopeIsCurrent = walletIsConnected && walletIsAuthenticated && currentConnectionRef.current &&
        currentAuthenticationRef.current &&
        !!committed && committed.wallet === currentWalletRef.current &&
        committed.identity === identity &&
        committed.token === deviceSessionToken && committed.key === v2KeyBytes;
    // Do not expose a just-stale ready bundle during the render in which an
    // account/provider changed; the effect will start the replacement work.
    const exposedReady = status === 'ready' && scopeIsCurrent;
    const value = useMemo(() => ({
        wallet: exposedReady ? currentWalletRef.current : null,
        identity: exposedReady ? identity : null,
        provider: exposedReady ? walletProvider : null,
        deviceId: exposedReady ? identity?.deviceId || null : null,
        deviceSessionToken: exposedReady ? deviceSessionToken : null,
        v2KeyBytes: exposedReady ? v2KeyBytes : null,
        keyReady: exposedReady && !!v2KeyBytes,
        status: !walletIsAuthenticated ? 'idle' : (exposedReady || status !== 'ready' ? status : 'idle'),
        error: walletIsAuthenticated ? error : null, retryProvision: provision, renewDeviceSession,
        listDevices: async (signWalletMessage) => selectedAccount ? listWalletDevices(selectedAccount, signWalletMessage) : unavailable(),
        revokeDevice: async (deviceId, signWalletMessage) => {
            if (!selectedAccount)
                return unavailable();
            await revokeWalletDevice(selectedAccount, deviceId, signWalletMessage);
        },
    }), [identity, walletProvider, deviceSessionToken, v2KeyBytes, status, exposedReady, error, walletIsAuthenticated, provision, renewDeviceSession, selectedAccount]);
    return _jsx(ChatSecurityContext.Provider, { value: value, children: children });
};
